import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { InventoryProvider, useInventory } from '../../../contexts/InventoryContext';
import DeveloperList from './DeveloperList';
import AddNewDeveloper from './AddNewDeveloper';
import AddNewProject from '../projects/AddNewProject';

/**
 * Developers page: list + Add Developer drawer + Edit drawer + Add Project drawer.
 * Wrap with InventoryProvider so list and add flow have access to developers/projects.
 */
function DevelopersPage() {
  const navigate = useNavigate();
  const {
    createDeveloper,
    createProject,
    updateDeveloper,
    deleteDeveloper,
    refetchOptions,
    refetchProjects,
  } = useInventory();

  const [addDrawerOpen, setAddDrawerOpen] = useState(false);
  const [editDeveloper, setEditDeveloper] = useState(null);
  const [addProjectDeveloper, setAddProjectDeveloper] = useState(null);

  const handleOpenProjects = (developer) => {
    if (developer?.id) navigate(`/inventory/projects?developer=${developer.id}`);
  };

  const handleEditDeveloper = (developer) => {
    setEditDeveloper(developer);
  };

  const handleAddProjectForDeveloper = (developer) => {
    setAddProjectDeveloper(developer);
  };

  const handleDeleteDeveloper = async (developer) => {
    if (!developer?.id) return;
    try {
      await deleteDeveloper(developer.id);
      refetchOptions();
    } catch (_) {}
  };

  const buildProjectPayload = async (data) => {
    let developerId = data.developer_id != null ? (Number(data.developer_id) || data.developer_id) : null;
    if (!developerId && data.developer_name?.trim() && typeof createDeveloper === 'function') {
      try {
        const created = await createDeveloper({ name: data.developer_name.trim() });
        developerId = created?.id ?? null;
      } catch (_) {
        return null;
      }
    }
    return {
      name: (data.name && String(data.name).trim()) || '',
      developer: developerId,
      construction_status: data.status || 'under_construction',
      description: (data.description && String(data.description).trim()) || '',
      commission_rate: data.commission_rate != null ? parseFloat(data.commission_rate) : null,
      commission_type: data.commission_type || 'percentage',
      price_min: data.price_min ?? null,
      price_max: data.price_max ?? null,
      total_area: data.total_area != null && data.total_area !== '' ? parseFloat(data.total_area) : null,
      area_unit: data.area_unit || 'sqft',
      launch_date: data.launch_date || null,
      possession_date: data.possession_date || null,
      completion_date: data.completion_date || null,
      rera_no: (data.rera_no && String(data.rera_no).trim()) || null,
      tags: Array.isArray(data.tags) ? data.tags : [],
      key_highlights: Array.isArray(data.key_highlights) ? data.key_highlights : [],
      metadata: data.metadata && typeof data.metadata === 'object' ? data.metadata : {},
      ...(data.address && typeof data.address === 'object' ? { address: data.address } : {}),
    };
  };

  const handleProjectSubmit = async (data) => {
    const payload = await buildProjectPayload(data);
    if (!payload || typeof createProject !== 'function') return;
    try {
      if (data.thumbnailFile instanceof File) {
        const formData = new FormData();
        Object.entries(payload).forEach(([key, value]) => {
          if (value === null || value === undefined) return;
          if (key === 'address') {
            formData.append(key, JSON.stringify(value));
          } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
            formData.append(key, JSON.stringify(value));
          } else if (Array.isArray(value)) {
            formData.append(key, JSON.stringify(value));
          } else {
            formData.append(key, String(value));
          }
        });
        if (payload.address == null && data.address) {
          formData.append('address', JSON.stringify(data.address));
        }
        formData.append('thumbnail', data.thumbnailFile);
        await createProject(formData);
      } else {
        await createProject(payload);
      }
      refetchProjects();
      refetchOptions();
      setAddProjectDeveloper(null);
    } catch (_) {}
  };

  return (
    <Box sx={{ p: 3 }}>
      <DeveloperList
        onAddDeveloper={() => setAddDrawerOpen(true)}
        onOpenProjects={handleOpenProjects}
        onEditDeveloper={handleEditDeveloper}
        onAddProjectForDeveloper={handleAddProjectForDeveloper}
        onDeleteDeveloper={handleDeleteDeveloper}
      />
      <AddNewDeveloper
        open={addDrawerOpen || Boolean(editDeveloper)}
        onClose={() => {
          setAddDrawerOpen(false);
          setEditDeveloper(null);
        }}
        initialData={editDeveloper}
      />
      <AddNewProject
        open={Boolean(addProjectDeveloper)}
        onClose={() => setAddProjectDeveloper(null)}
        onSubmit={handleProjectSubmit}
        initialDeveloper={addProjectDeveloper}
      />
    </Box>
  );
}

export default function Developers() {
  return (
    <InventoryProvider>
      <DevelopersPage />
    </InventoryProvider>
  );
}
